/**
 * Notification Settings Tab Component
 * Configure alerts and notification preferences
 */
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Bell, Save } from "lucide-react";

export function NotificationSettingsTab({
  settings,
  onChange,
  onSave,
  onTestNotification,
  saving,
  testingNotification
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="notification-settings-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Notification Preferences</h2>
          <p className="text-sm text-slate-500">Configure how you receive alerts and reminders</p>
        </div>
        <Button onClick={onSave} disabled={saving} className="gap-2">
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="font-medium text-slate-800">Delivery Methods</h3>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Email Notifications</p>
                <p className="text-sm text-slate-500">Receive updates via email</p>
              </div>
              <Switch
                checked={settings.email_notifications || false}
                onCheckedChange={(checked) => 
                  onChange({ ...settings, email_notifications: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">SMS Notifications</p>
                <p className="text-sm text-slate-500">Receive text messages for urgent alerts</p>
              </div>
              <Switch
                checked={settings.sms_notifications || false}
                onCheckedChange={(checked) => 
                  onChange({ ...settings, sms_notifications: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Daily Digest</p>
                <p className="text-sm text-slate-500">Receive a summary email each morning</p>
              </div>
              <Switch
                checked={settings.daily_digest || false}
                onCheckedChange={(checked) => 
                  onChange({ ...settings, daily_digest: checked })
                }
              />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-medium text-slate-800">Alert Types</h3>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Appointment Reminders</p>
                <p className="text-sm text-slate-500">Remind staff of upcoming visits</p>
              </div>
              <Switch
                checked={settings.appointment_reminders || false}
                onCheckedChange={(checked) => 
                  onChange({ ...settings, appointment_reminders: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Urgent Alerts</p>
                <p className="text-sm text-slate-500">Immediate notifications for critical items</p>
              </div>
              <Switch
                checked={settings.urgent_alerts || false}
                onCheckedChange={(checked) => 
                  onChange({ ...settings, urgent_alerts: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Referral Updates</p>
                <p className="text-sm text-slate-500">Notify when referral status changes</p>
              </div>
              <Switch
                checked={settings.referral_updates || false}
                onCheckedChange={(checked) => 
                  onChange({ ...settings, referral_updates: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Document Expiry Alerts</p>
                <p className="text-sm text-slate-500">Warn about expiring certifications</p>
              </div>
              <Switch
                checked={settings.document_expiry_alerts || false}
                onCheckedChange={(checked) => 
                  onChange({ ...settings, document_expiry_alerts: checked })
                }
              />
            </div>
          </div>
        </div>

        <div className="pt-4 border-t">
          <div className="space-y-2 max-w-xs">
            <Label>Reminder Lead Time (hours)</Label>
            <Select
              value={String(settings.reminder_hours_before || 24)}
              onValueChange={(val) => 
                onChange({ ...settings, reminder_hours_before: parseInt(val) })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 hour before</SelectItem>
                <SelectItem value="2">2 hours before</SelectItem>
                <SelectItem value="12">12 hours before</SelectItem>
                <SelectItem value="24">24 hours before</SelectItem>
                <SelectItem value="48">48 hours before</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* EVV Notifications Section */}
        <div className="pt-4 border-t mt-6">
          <h3 className="font-medium text-slate-800 mb-4">EVV Notifications</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">EVV Alerts</p>
                <p className="text-sm text-slate-500">Master toggle for all EVV notifications</p>
              </div>
              <Switch
                checked={settings.evv_alerts || false}
                onCheckedChange={(checked) => 
                  onChange({ ...settings, evv_alerts: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Flagged Visit Alerts</p>
                <p className="text-sm text-slate-500">Notify when a visit is flagged</p>
              </div>
              <Switch
                checked={settings.evv_flagged_alerts || false}
                onCheckedChange={(checked) => 
                  onChange({ ...settings, evv_flagged_alerts: checked })
                }
                disabled={!settings.evv_alerts}
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Missed Visit Alerts</p>
                <p className="text-sm text-slate-500">Notify when appointments are not clocked</p>
              </div>
              <Switch
                checked={settings.evv_missed_alerts || false}
                onCheckedChange={(checked) => 
                  onChange({ ...settings, evv_missed_alerts: checked })
                }
                disabled={!settings.evv_alerts}
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Daily EVV Summary</p>
                <p className="text-sm text-slate-500">Receive daily EVV statistics</p>
              </div>
              <Switch
                checked={settings.evv_daily_summary || false}
                onCheckedChange={(checked) => 
                  onChange({ ...settings, evv_daily_summary: checked })
                }
                disabled={!settings.evv_alerts}
              />
            </div>
          </div>

          {/* Test Notification Button */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-blue-800">Test Notifications</p>
                <p className="text-sm text-blue-600">Send a test notification to verify settings (MOCKED)</p>
              </div>
              <Button
                variant="outline"
                onClick={onTestNotification}
                disabled={testingNotification}
                className="gap-2"
              >
                <Bell className="w-4 h-4" />
                {testingNotification ? "Sending..." : "Send Test"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
